import { Element } from "@craftjs/core";
import { Box, Button } from "components/ui";
import { ChevronLeft, ChevronRight } from "lucide-react-native";
import React, { useCallback, useState } from "react";
import { Dimensions, Platform, View } from "react-native";
import Carousel from "react-native-reanimated-carousel";
import { Carousel as WebCarousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { PageContainer } from "./PageContainer";

interface PanelDropLocationProps {
	children: any;
	autoPlay?: boolean;
	autoPlayInterval?: number;
	showIndicators?: boolean;
	showArrows?: boolean;
	infiniteLoop?: boolean;
	arrowPosition?: "bottom" | "center" | "custom";
	arrowCustomPosition?: { bottom?: number; left?: number };
	[key: string]: any;
}

export const PanelDropLocation = ({ children, autoPlay = false, autoPlayInterval = 3000, showIndicators = false, showArrows = true, infiniteLoop = true, arrowPosition = "bottom", arrowCustomPosition }: PanelDropLocationProps) => {
	const width = Dimensions.get("window").width;
	const childrenArray = React.Children.toArray(children?.props?.children || []);
	const carouselRef: any = React.useRef(null);
	const isMobile = useIsMobile();
	const [currentIndex, setCurrentIndex] = useState(0);

	// Handle cases where children might not be available immediately
	const hasChildren = childrenArray.length > 0;

	// Navigation handlers for web carousel
	const handlePrev = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.decrement();
		}
	}, []);

	const handleNext = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.increment();
		}
	}, []);

	// Navigation handlers for native carousel
	const handleNativePrev = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.scrollTo({ count: -1, animated: true });
		}
	}, []);

	const handleNativeNext = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.scrollTo({ count: 1, animated: true });
		}
	}, []);

	const handleSlideChange = useCallback((index: number) => {
		setCurrentIndex(index);
	}, []);

	// Calculate arrow positioning based on selected position
	const getArrowStyles = () => {
		if (arrowPosition === "center") {
			return {
				position: "absolute",
				bottom: "50%",
				transform: [{ translateY: 18 }],
				width: "100%",
				justifyContent: "space-between",
				paddingHorizontal: 16,
				flexDirection: "row" as const,
				zIndex: 10,
			};
		} else if (arrowPosition === "custom" && arrowCustomPosition) {
			return {
				position: "absolute",
				bottom: arrowCustomPosition.bottom || 20,
				left: arrowCustomPosition.left || "8%",
				flexDirection: "row" as const,
				gap: 8,
				zIndex: 10,
			};
		} else {
			// Default bottom position
			return {
				position: "absolute",
				bottom: isMobile ? 40 : 70,
				left: isMobile ? 24 : 47,
				flexDirection: "row" as const,
				gap: 8,
				zIndex: 10,
			};
		}
	};

	// Mobile Native Implementation
	if (isMobile && Platform.OS !== "web") {
		if (!hasChildren) {
			return null;
		}

		return (
			<View style={{ flex: 1, position: "relative", width: "100%" }}>
				<Carousel
					loop={infiniteLoop}
					ref={carouselRef}
					width={width}
					style={{ width: "100%" }}
					height={578}
					panGestureHandlerProps={{
						activeOffsetX: [-10, 10],
					}}
					autoPlay={autoPlay}
					autoPlayInterval={autoPlayInterval}
					// minScrollDistancePerSwipe={0.1}
					data={childrenArray}
					scrollAnimationDuration={1000}
					onSnapToItem={handleSlideChange}
					renderItem={({ item }) => <View style={{ height: "100%" }}>{item}</View>}
				/>

				{showArrows && (
					// @ts-ignore
					<Box position="relative">
						<Box {...getArrowStyles()}>
							<Button backgroundColor="white" borderWidth={1} w={36} h={36} padding={0} borderRadius={12} onPress={handleNativePrev}>
								<ChevronLeft color="black" />
							</Button>
							<Button backgroundColor="white" borderWidth={1} padding={0} w={36} h={36} borderRadius={12} onPress={handleNativeNext}>
								<ChevronRight color="black" />
							</Button>
						</Box>
					</Box>
				)}

				{showIndicators && childrenArray.length > 1 && (
					<Box position="absolute" bottom={40} width="100%" flexDirection="row" justifyContent="center" gap={8}>
						{childrenArray.map((_, index) => (
							<Box key={`indicator-${index}`} width={8} height={8} borderRadius={4} backgroundColor={currentIndex === index ? "black" : "gray"} />
						))}
					</Box>
				)}
			</View>
		);
	}

	// Web Implementation
	if (!hasChildren) {
		return null;
	}

	return (
		<Box marginVertical={12}>
			<Box position="relative">
				<WebCarousel
					ref={carouselRef}
					showIndicators={showIndicators}
					showStatus={false}
					className="carousel_custom"
					showThumbs={false}
					showArrows={false}
					autoPlay={autoPlay}
					interval={autoPlayInterval}
					infiniteLoop={infiniteLoop}
					onChange={handleSlideChange}
				>
					{childrenArray.map((child, key) => (
						<Box key={key}>{child}</Box>
					))}
				</WebCarousel>

				{showArrows && childrenArray.length > 1 && (
					// @ts-ignore
					<Box {...getArrowStyles()}>
						<Button onPress={handlePrev} backgroundColor="white" borderWidth={1} w={40} h={40} padding={0} borderRadius={12}>
							<ChevronLeft color="black" />
						</Button>
						<Button onPress={handleNext} backgroundColor="white" borderWidth={1} w={40} h={40} padding={0} borderRadius={12}>
							<ChevronRight color="black" />
						</Button>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export const PageImageSlider = ({ autoPlay, autoPlayInterval, showIndicators, showArrows, infiniteLoop, arrowPosition, arrowCustomPosition, ...props }: Partial<PanelDropLocationProps>) => {
	return (
		<PageContainer background={"white"} {...props}>
			<Box width={"$full"}>
				{/* @ts-ignore */}
				<Element
					id="image-slider-panel-drop-location"
					is={PanelDropLocation}
					data-cy="image-slider-panel-drop-location"
					autoPlay={autoPlay}
					autoPlayInterval={autoPlayInterval}
					showIndicators={showIndicators}
					showArrows={showArrows}
					infiniteLoop={infiniteLoop}
					arrowPosition={arrowPosition}
					arrowCustomPosition={arrowCustomPosition}
				></Element>
			</Box>
		</PageContainer>
	);
};
